import { useState } from 'react';
import PageHeader from '../components/PageHeader';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

const faqSections: FAQSection[] = [
  {
    title: 'Getting Started',
    items: [
      {
        question: 'What is GuardGrade?',
        answer:
          'GuardGrade is a neutral security performance scoring platform. Vendors upload Daily Activity Reports (DARs), AI extracts structured events, and a rule engine scores guards, sites, and vendors weekly. Clients see objective performance metrics.',
      },
      {
        question: 'What do the score colors mean?',
        answer:
          'GuardGrade scores range from 300 to 900. Strong Observed Performance (750-900, green): the most consistent reporting and operational signal we observe. Stable Observed Performance (650-749, yellow): solid but with room to strengthen. Developing / Mixed Performance (300-649, red): early or inconsistent reporting where more data is still building. All entities start at a baseline score of 720.',
      },
      {
        question: 'How often are scores updated?',
        answer:
          'Scores are calculated weekly. Daily activity reports are aggregated nightly, and the scoring engine runs once per week to produce new scores based on active rules.',
      },
    ],
  },
  {
    title: 'For Vendors',
    items: [
      {
        question: 'How do I upload a Daily Activity Report?',
        answer:
          'Go to Upload Reports in the sidebar. Drag and drop your file or click to select it, choose the site, and click Upload. Supported formats: PDF, CSV, Excel, DOCX, TXT, and images. The AI will automatically parse the report and extract events.',
      },
      {
        question: 'How do I set up email ingestion?',
        answer:
          "Each site has a unique ingestion email address (find it on the site detail page). Add your authorized sender emails in Organization \u2192 Email Whitelist. Once set up, just email your DARs to the site address and they'll be processed automatically.",
      },
      {
        question: "Why is my guard showing as 'unmatched'?",
        answer:
          "When a DAR is uploaded, the AI extracts guard names and tries to match them to your guard roster. If the name doesn't match any guard, you'll get a notification. Go to Guards and make sure the guard is added with the correct name.",
      },
      {
        question: 'How does billing work?',
        answer:
          'Your plan determines how many guards, sites, and uploads per month you can have. View your current usage in Billing & Plans. You can upgrade or downgrade at any time. The Starter plan is free with limited usage.',
      },
      {
        question: 'What file formats are supported for uploads?',
        answer:
          'PDF, CSV, Excel (.xlsx), Word (.docx), plain text (.txt), and images (PNG, JPG). Maximum file size is 50 MB.',
      },
    ],
  },
  {
    title: 'For Clients',
    items: [
      {
        question: 'How do I add a vendor to my site?',
        answer:
          'First, browse the Vendor Directory to find a vendor. Send them a connection request. Once they accept, go to your site and assign the vendor. The vendor can then start uploading DARs for that site.',
      },
      {
        question: 'What does my site score mean?',
        answer:
          "Your site score reflects how well the assigned vendor's guards are performing at that site. It's based on patrol frequency, incident response, report quality, and other metrics defined by the scoring rules.",
      },
      {
        question: "How do I view my vendor's performance?",
        answer:
          'Go to My Vendors and click on a vendor to see their overall score, the sites they serve for you, and their contact information. You can also view detailed analytics in the Analytics page.',
      },
      {
        question: 'Can I cancel a vendor relationship?',
        answer:
          'Yes. Go to the vendor detail page and click "Cancel Vendor Relationship." This will remove the vendor from your sites. You can then assign a different vendor.',
      },
    ],
  },
  {
    title: 'For Admins',
    items: [
      {
        question: 'How do I create a scoring rule?',
        answer:
          'Go to Rules and click "New Rule." Fill in the rule name, category, target score type (guard/site/vendor), conditions using the condition builder, and the score impact. You can test the rule before activating it.',
      },
      {
        question: 'How do I run scoring manually?',
        answer:
          'On the Admin Dashboard, click "Run Manual Scoring." This triggers the weekly scoring cycle immediately. Alternatively, run `python manage.py run_weekly_scoring` from the command line.',
      },
      {
        question: 'How do I reprocess a failed upload?',
        answer:
          'Go to Ingestion, find the failed upload or email ingestion, and click "Reprocess." This will re-run the AI parsing pipeline on the original file.',
      },
    ],
  },
];

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${
        open ? 'rotate-90' : ''
      }`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

export default function HelpPage() {
  const [openSections, setOpenSections] = useState<Record<number, boolean>>(() =>
    faqSections.reduce(
      (acc, _, idx) => ({ ...acc, [idx]: true }),
      {} as Record<number, boolean>,
    ),
  );
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  const toggleSection = (idx: number) => {
    setOpenSections((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const toggleQuestion = (key: string) => {
    setOpenQuestion((prev) => (prev === key ? null : key));
  };

  return (
    <div>
      <PageHeader title="Help & FAQ" description="Answers to common questions" />

      <div className="space-y-6">
        {faqSections.map((section, sIdx) => (
          <div
            key={section.title}
            className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
          >
            {/* Section header */}
            <button
              type="button"
              onClick={() => toggleSection(sIdx)}
              className="w-full flex items-center justify-between px-6 py-4 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <span className="text-lg font-semibold text-slate-900">
                {section.title}
              </span>
              <ChevronIcon open={openSections[sIdx]} />
            </button>

            {/* Questions */}
            <div
              className={`transition-all duration-300 ease-in-out ${
                openSections[sIdx]
                  ? 'max-h-[2000px] opacity-100'
                  : 'max-h-0 opacity-0 overflow-hidden'
              }`}
            >
              <div className="divide-y divide-slate-100">
                {section.items.map((item, qIdx) => {
                  const key = `${sIdx}-${qIdx}`;
                  const isOpen = openQuestion === key;

                  return (
                    <div key={key}>
                      <button
                        type="button"
                        onClick={() => toggleQuestion(key)}
                        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="text-sm font-medium text-slate-800 pr-4">
                          {item.question}
                        </span>
                        <ChevronIcon open={isOpen} />
                      </button>

                      <div
                        className={`transition-all duration-200 ease-in-out ${
                          isOpen
                            ? 'max-h-[500px] opacity-100'
                            : 'max-h-0 opacity-0 overflow-hidden'
                        }`}
                      >
                        <p className="px-6 pb-4 text-sm text-slate-600 leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
